import React from "react";
import { Button, Empty, Link, Spin, Table, Tag, Typography } from "@arco-design/web-react";
import { IconRefresh } from "@arco-design/web-react/icon";
import { useAuth0 } from "@auth0/auth0-react";
import moment from "moment";
import { FetchApi } from "../Api/AuthenticatedApi";
import { STALE_DAYS, isStale, isTestPage, shortPage } from "./formPlacements";

const { Text, Paragraph } = Typography;

// Lists the pages this signup's embedded form has been seen on, so its owner
// can find where they put it. A page shows up once someone visits it.
export default function FormPlacementsCard({ campaignId }) {
  const { getAccessTokenSilently } = useAuth0();
  const [placements, setPlacements] = React.useState(null);
  const [error, setError] = React.useState("");

  const load = React.useCallback(() => {
    setError("");
    FetchApi(
      getAccessTokenSilently,
      `/api/campaigns/${campaignId}/placements`,
      (data) => setPlacements(Array.isArray(data) ? data : []),
      () => {},
      (err) => setError((err && err.message) || "Couldn't load the pages")
    );
  }, [getAccessTokenSilently, campaignId]);

  React.useEffect(load, [load]);

  if (!campaignId) return null;

  const columns = [
    {
      title: "Page",
      render: (_, p) => (
        <span>
          <Link href={p.page_url} target="_blank" rel="noreferrer" style={{ wordBreak: "break-all" }}>{shortPage(p.page_url)}</Link>
          {isTestPage(p.page_url) && <Tag size="small" style={{ marginLeft: 6 }}>test page</Tag>}
          {isStale(p) && <Tag size="small" color="orange" style={{ marginLeft: 6 }} title={`Not seen for over ${STALE_DAYS} days`}>may be removed</Tag>}
        </span>
      ),
    },
    {
      title: "Last seen",
      width: 150,
      render: (_, p) => <span title={moment(p.last_seen_date).format("MMM D, YYYY h:mm A")}>{moment(p.last_seen_date).fromNow()}</span>,
    },
    {
      title: "First seen",
      width: 130,
      render: (_, p) => moment(p.first_seen_date).format("MMM D, YYYY"),
    },
    {
      title: <span title="Every time the form loaded, reloads included">Impressions</span>,
      dataIndex: "impressions", width: 110, align: "right", render: (n) => n || 0,
    },
    { title: <span title="Each visitor once">Views</span>, dataIndex: "views", width: 90, align: "right" },
    { title: "Signups", dataIndex: "signups", width: 90, align: "right", render: (n) => n || 0 },
  ];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 8 }}>
        <Paragraph type="secondary" style={{ margin: 0, flex: 1 }}>
          Pages where this form has been seen. A page shows up the first time the form loads on it.
          Impressions count every load; views and signups count each visitor once, as the form's own counts do.
        </Paragraph>
        <Button size="small" icon={<IconRefresh />} onClick={load}>Refresh</Button>
      </div>
      {error && <Text type="error">{error}</Text>}
      {placements === null && !error ? (
        <Spin />
      ) : (
        <Table
          size="small"
          rowKey="id"
          columns={columns}
          data={placements || []}
          pagination={placements && placements.length > 20 ? { pageSize: 20 } : false}
          noDataElement={<Empty description="Not seen on any page yet. Paste the embed code on a page, then visit it." />}
        />
      )}
    </div>
  );
}
